// Campo de tempo digitado (estimativa "Outro", foco do Pomodoro e "Tempo de foco" das
// Configurações): o número e, ao lado, a unidade — minutos ou horas — como no "Outro tempo" do
// descanso do Pomodoro (AC-246). Em minutos aceita "45", "1:30", "1h30"; em horas, "1", "1,5" e
// também "1:30". Um número acima de 59 em minutos desliza para horas ao parar de digitar (~1 s)
// ou sair do campo, com animação de troca: 120 → "2" horas, 130 → "2:10" horas (AC-186 / AC-068).
// O valor em minutos não muda.
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { parseEstimate } from '../domain/task';
import { cls } from './NewTaskForm';

interface Props {
  label: string;
  placeholder?: string;
  /** minutos já escolhidos (mostrados ao abrir) */
  initialMinutes: number | null;
  /** minutos digitados; null = vazio; -1 = texto que não dá para entender */
  onChange(minutes: number | null): void;
  disabled?: boolean;
  className?: string;
}

export type MinutesUnit = 'min' | 'h';

export const CONVERT_AFTER_MS = 1000;

/** Minutos do texto na unidade escolhida; null = vazio; -1 = não dá para entender. */
export function toMinutes(text: string, unit: MinutesUnit): number | null {
  const raw = text.trim();
  if (!raw) return null;
  if (unit === 'h') {
    const m = /^(\d{1,2})(?:[.,](\d+))?$/.exec(raw);
    if (m) return Math.round(Number(`${m[1]}.${m[2] ?? '0'}`) * 60);
  }
  // "1:30" e "1h30" já dizem a unidade; em minutos, "45" é 45
  if (unit === 'h' && /^\d+$/.test(raw)) return -1;
  return parseEstimate(raw) ?? -1;
}

/** Como mostrar minutos já escolhidos: até 59 em minutos; acima, em horas ("2" ou "2:10"). */
export function showMinutes(minutes: number | null): { text: string; unit: MinutesUnit } {
  if (minutes === null || minutes < 0) return { text: '', unit: 'min' };
  if (minutes < 60) return { text: String(minutes), unit: 'min' };
  const h = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return { text: rest ? `${h}:${String(rest).padStart(2, '0')}` : String(h), unit: 'h' };
}

export function MinutesInput({ label, placeholder, initialMinutes, onChange, disabled, className }: Props) {
  const { t } = useTranslation();
  const initial = showMinutes(initialMinutes);
  const [text, setText] = useState(initial.text);
  const [unit, setUnit] = useState<MinutesUnit>(initial.unit);
  const [morphing, setMorphing] = useState(false);
  const typed = useRef(text);
  const unitRef = useRef(unit);
  const timers = useRef<number[]>([]);
  const clearTimers = () => {
    timers.current.forEach((id) => window.clearTimeout(id));
    timers.current = [];
  };
  useEffect(() => clearTimers, []);

  const convert = () => {
    clearTimers();
    const raw = typed.current.trim();
    if (unitRef.current !== 'min' || !/^\d+$/.test(raw)) return;
    const minutes = Number(raw);
    if (minutes < 60) return;
    const next = showMinutes(minutes);
    setMorphing(true);
    timers.current.push(
      window.setTimeout(() => {
        setText(next.text);
        setUnit(next.unit);
        typed.current = next.text;
        unitRef.current = next.unit;
      }, 160)
    );
    timers.current.push(window.setTimeout(() => setMorphing(false), 340));
  };

  return (
    <span className={cls('duration', className)}>
      <input
        className={cls('input input--chip input--minutes', morphing && 'is-morphing')}
        aria-label={label}
        placeholder={placeholder}
        inputMode="decimal"
        value={text}
        disabled={disabled}
        onChange={(e) => {
          setText(e.target.value);
          typed.current = e.target.value;
          onChange(toMinutes(e.target.value, unitRef.current));
          clearTimers();
          timers.current.push(window.setTimeout(convert, CONVERT_AFTER_MS));
        }}
        onBlur={convert}
      />
      <select
        className={cls('input input--unit', morphing && 'is-morphing')}
        aria-label={`${t('pomodoro.unitLabel')}: ${label}`}
        value={unit}
        disabled={disabled}
        onChange={(e) => {
          const u = e.target.value as MinutesUnit;
          clearTimers();
          setUnit(u);
          unitRef.current = u;
          onChange(toMinutes(typed.current, u));
        }}
      >
        <option value="min">{t('pomodoro.unitMinutes')}</option>
        <option value="h">{t('pomodoro.unitHours')}</option>
      </select>
    </span>
  );
}
